// this file cannot include any non-type imports; see https://rustwasm.github.io/wasm-bindgen/reference/js-snippets.html#caveats

import type { ConnectionKind, JsSnackbarSeverity, TransactionName } from '@/app/quadratic-core-types';

declare var self: WorkerGlobalScope &
  typeof globalThis & {
    addUnsentTransaction: (transactionId: string, transaction: string, operations: number) => void;
    sendTransaction: (
      transactionId: string,
      operations: ArrayBufferLike,
      provenance?: string,
      historyMovements?: string,
      cellSources?: string
    ) => void;
    sendImportProgress: (filename: string, current: number, total: number) => void;
    sendAddSheetClient: (sheetInfo: Uint8Array, user: boolean) => void;
    sendAddSheetRender: (sheetInfo: Uint8Array) => void;
    sendDeleteSheetClient: (sheetId: string, user: boolean) => void;
    sendSheetsInfoClient: (sheetsInfo: Uint8Array) => void;
    sendSheetsInfoRender: (sheetsInfo: Uint8Array) => void;
    sendSheetInfoUpdateClient: (sheetInfo: Uint8Array) => void;
    sendSheetInfoUpdateRender: (sheetInfo: Uint8Array) => void;
    sendA1Context: (context: Uint8Array) => void;
    sendHashRenderFills: (hashRenderFills: Uint8Array) => void;
    sendHashesDirtyFills: (dirtyHashes: Uint8Array) => void;
    sendSheetMetaFills: (sheetId: string, fills: Uint8Array) => void;
    sendBordersSheet: (sheetId: string, borders: Uint8Array) => void;
    sendDeleteSheetRender: (sheetId: string) => void;
    sendSetCursor: (cursor: string) => void;
    requestTransactions: (sequenceNum: number) => void;
    sendSheetOffsetsClient: (sheetId: string, offsets: Uint8Array) => void;
    sendSheetOffsetsRender: (sheetId: string, offsets: Uint8Array) => void;
    sendSheetHtml: (html: Uint8Array) => void;
    sendUpdateHtml: (html: Uint8Array) => void;
    sendGenerateThumbnail: () => void;
    sendSheetCodeCells: (sheetId: string, renderCodeCells: Uint8Array) => void;
    sendSheetBoundsUpdateClient: (sheetBounds: Uint8Array) => void;
    sendSheetBoundsUpdateRender: (sheetBounds: Uint8Array) => void;
    sendTransactionStartClient: (transactionId: string, transactionName: TransactionName) => void;
    sendTransactionStartRender: (transactionId: string, transactionName: TransactionName) => void;
    sendTransactionEndClient: (transactionId: string, transactionName: TransactionName) => void;
    sendTransactionEndRender: (transactionId: string, transactionName: TransactionName) => void;
    sendCellEditHistory: (history: string) => void;
    sendRunPython: (
      transactionId: string,
      x: number,
      y: number,
      sheetId: string,
      code: string,
      chartPixelWidth: number,
      chartPixelHeight: number
    ) => void;
    sendRunJavascript: (transactionId: string, x: number, y: number, sheetId: string, code: string) => void;
    sendUpdateCodeCells: (updateCodeCells: Uint8Array) => void;
    sendRequestCodeSummaries: (summaries: Uint8Array) => void;
    sendUndoRedo: (undo: string, redo: string) => void;
    sendConnection: (
      transactionId: string,
      x: number,
      y: number,
      sheetId: string,
      code: string,
      connector_type: ConnectionKind,
      connection_id: string
    ) => void;
    sendImage: (sheetId: string, x: number, y: number, image?: string, w?: string, h?: string) => void;
    sendSheetValidations: (sheetId: string, sheetValidations: Uint8Array) => void;
    sendSheetConditionalFormats: (sheetId: string, conditionalFormats: Uint8Array) => void;
    sendSheetComments: (sheetId: string, comments: Uint8Array) => void;
    sendSheetCharts: (sheetId: string, charts: Uint8Array) => void;
    sendValidationWarnings: (warnings: Uint8Array) => void;
    sendRequestRowHeights: (transactionId: string, sheetId: string, rows: string) => void;
    sendMultiplayerSynced: () => void;
    sendHashRenderCellsRender: (hashRenderCells: Uint8Array) => void;
    sendHashesDirtyRender: (dirtyHashes: Uint8Array) => void;
    sendViewportBuffer: (buffer: SharedArrayBuffer) => void;
    sendClientMessage: (message: string, severity: JsSnackbarSeverity) => void;
    sendDataTablesCache: (sheetId: string, dataTablesCache: Uint8Array) => void;
    sendContentCache: (sheetId: string, contentCache: Uint8Array) => void;
    sendMergeCells: (sheetId: string, mergeCells: Uint8Array) => void;
    sendMergeCellsRender: (sheetId: string, mergeCells: Uint8Array, dirtyHashes: Uint8Array) => void;
    sendCodeRunningState: (transactionId: string, codeOperations: string) => void;
    sendPendingCellsState: (pendingCells: string) => void;
    getCachedViewportJson: () => string | null;
  };

// These functions are invoked *by Rust* (they are the wasm-bindgen import
// boundary). A callback must never throw back into wasm: a JS exception
// propagates through the wasm frames without running Rust destructors, which
// leaves the GridController's wasm-bindgen `WasmRefCell` borrow permanently
// locked. That poisoned borrow then surfaces on the *next* call into core as a
// spurious "recursive use of an object detected which would lead to unsafe
// aliasing in rust" error (e.g. a later getEditCell) — an unrelated, confusing
// crash that disables the whole document. `guard`/`guardReturn` swallow and log
// so a single failed render/notification (e.g. a `postMessage` that throws
// under memory pressure) can't poison the instance.
function guard<A extends unknown[]>(name: string, fn: (...args: A) => void): (...args: A) => void {
  return (...args: A) => {
    try {
      fn(...args);
    } catch (error) {
      console.error(`[rustCallbacks] ${name} threw; swallowed to protect the wasm borrow`, error);
    }
  };
}

function guardReturn<A extends unknown[], R>(name: string, fn: (...args: A) => R, fallback: R): (...args: A) => R {
  return (...args: A) => {
    try {
      return fn(...args);
    } catch (error) {
      console.error(`[rustCallbacks] ${name} threw; returning fallback to protect the wasm borrow`, error);
      return fallback;
    }
  };
}

export const addUnsentTransaction = guard(
  'addUnsentTransaction',
  (transactionId: string, transactions: string, operations: number) => {
    self.addUnsentTransaction(transactionId, transactions, operations);
  }
);

export const jsSendTransaction = guard(
  'jsSendTransaction',
  (
    transactionId: string,
    operations: Uint8Array,
    provenance?: string,
    historyMovements?: string,
    cellSources?: string
  ) => {
    self.sendTransaction(transactionId, operations.buffer, provenance, historyMovements, cellSources);
  }
);

export const jsTime = guard('jsTime', (name: string) => console.time(name));
export const jsTimeEnd = guard('jsTimeEnd', (name: string) => console.timeEnd(name));

export const jsImportProgress = guard('jsImportProgress', (filename: string, current: number, total: number) => {
  self.sendImportProgress(filename, current, total);
});

export const jsAddSheet = guard('jsAddSheet', (sheetInfo: Uint8Array, user: boolean) => {
  const clientCopy = new Uint8Array(sheetInfo);
  self.sendAddSheetClient(clientCopy, user);
  self.sendAddSheetRender(sheetInfo);
});

export const jsDeleteSheet = guard('jsDeleteSheet', (sheetId: string, user: boolean) => {
  self.sendDeleteSheetClient(sheetId, user);
  self.sendDeleteSheetRender(sheetId);
});

export const jsSheetInfo = guard('jsSheetInfo', (sheetInfo: Uint8Array) => {
  const clientCopy = new Uint8Array(sheetInfo);
  self.sendSheetsInfoClient(clientCopy);
  self.sendSheetsInfoRender(sheetInfo);
});

export const jsSheetInfoUpdate = guard('jsSheetInfoUpdate', (sheetInfo: Uint8Array) => {
  const clientCopy = new Uint8Array(sheetInfo);
  self.sendSheetInfoUpdateClient(clientCopy);
  self.sendSheetInfoUpdateRender(sheetInfo);
});

export const jsHashRenderFills = guard('jsHashRenderFills', (hashRenderFills: Uint8Array) => {
  self.sendHashRenderFills(hashRenderFills);
});

export const jsHashesDirtyFills = guard('jsHashesDirtyFills', (dirtyHashes: Uint8Array) => {
  self.sendHashesDirtyFills(dirtyHashes);
});

export const jsSheetMetaFills = guard('jsSheetMetaFills', (sheetId: string, sheetMetaFills: Uint8Array) => {
  self.sendSheetMetaFills(sheetId, sheetMetaFills);
});

export const jsOffsetsModified = guard('jsOffsetsModified', (sheetId: string, offsets: Uint8Array) => {
  const clientCopy = new Uint8Array(offsets);
  self.sendSheetOffsetsClient(sheetId, clientCopy);
  self.sendSheetOffsetsRender(sheetId, offsets);
});

export const jsHtmlOutput = guard('jsHtmlOutput', (html: Uint8Array) => {
  self.sendSheetHtml(html);
});

export const jsUpdateHtml = guard('jsUpdateHtml', (html: Uint8Array) => {
  self.sendUpdateHtml(html);
});

export const jsRequestTransactions = guard('jsRequestTransactions', (sequenceNum: bigint) => {
  self.requestTransactions(Number(sequenceNum));
});

export const jsSetCursor = guard('jsSetCursor', (cursor: string) => {
  self.sendSetCursor(cursor);
});

export const jsGenerateThumbnail = guard('jsGenerateThumbnail', () => {
  self.sendGenerateThumbnail();
});

export const jsBordersSheet = guard('jsBordersSheet', (sheetId: string, borders: Uint8Array) => {
  self.sendBordersSheet(sheetId, borders);
});

export const jsSheetCodeCells = guard('jsSheetCodeCells', (sheetId: string, renderCodeCells: Uint8Array) => {
  self.sendSheetCodeCells(sheetId, renderCodeCells);
});

export const jsSheetBoundsUpdate = guard('jsSheetBoundsUpdate', (bounds: Uint8Array) => {
  const clientCopy = new Uint8Array(bounds);
  self.sendSheetBoundsUpdateClient(clientCopy);
  self.sendSheetBoundsUpdateRender(bounds);
});

export const jsTransactionStart = guard('jsTransactionStart', (transaction_id: string, transaction_name: string) => {
  const transactionName = JSON.parse(transaction_name);
  self.sendTransactionStartClient(transaction_id, transactionName);
  self.sendTransactionStartRender(transaction_id, transactionName);
});

export const jsTransactionEnd = guard('jsTransactionEnd', (transaction_id: string, transaction_name: string) => {
  const transactionName = JSON.parse(transaction_name);
  self.sendTransactionEndClient(transaction_id, transactionName);
  self.sendTransactionEndRender(transaction_id, transactionName);
});

export const jsCellEditHistory = guard('jsCellEditHistory', (history: string) => {
  self.sendCellEditHistory(history);
});

export const jsRunPython = guard(
  'jsRunPython',
  (
    transactionId: string,
    x: number,
    y: number,
    sheetId: string,
    code: string,
    chartPixelWidth: number,
    chartPixelHeight: number
  ) => {
    self.sendRunPython(transactionId, x, y, sheetId, code, chartPixelWidth, chartPixelHeight);
  }
);

export const jsRunJavascript = guard(
  'jsRunJavascript',
  (transactionId: string, x: number, y: number, sheetId: string, code: string) => {
    self.sendRunJavascript(transactionId, x, y, sheetId, code);
  }
);

export const jsUpdateCodeCells = guard('jsUpdateCodeCells', (updateCodeCells: Uint8Array) => {
  self.sendUpdateCodeCells(updateCodeCells);
});

export const jsRequestCodeSummaries = guard('jsRequestCodeSummaries', (summaries: Uint8Array) => {
  self.sendRequestCodeSummaries(summaries);
});

export const jsUndoRedo = guard('jsUndoRedo', (undo: string, redo: string) => {
  self.sendUndoRedo(undo, redo);
});

export const jsConnection = guard(
  'jsConnection',
  (
    transactionId: string,
    x: number,
    y: number,
    sheetId: string,
    code: string,
    connector_type: ConnectionKind,
    connection_id: string
  ) => {
    self.sendConnection(transactionId, x, y, sheetId, code, connector_type, connection_id);
  }
);

export const jsSendImage = guard(
  'jsSendImage',
  (sheetId: string, x: number, y: number, image?: string, w?: string, h?: string) => {
    self.sendImage(sheetId, x, y, image, w, h);
  }
);

export const jsSheetValidations = guard('jsSheetValidations', (sheetId: string, sheetValidations: Uint8Array) => {
  self.sendSheetValidations(sheetId, sheetValidations);
});

export const jsSheetConditionalFormats = guard(
  'jsSheetConditionalFormats',
  (sheetId: string, conditionalFormats: Uint8Array) => {
    self.sendSheetConditionalFormats(sheetId, conditionalFormats);
  }
);

export const jsSheetComments = guard('jsSheetComments', (sheetId: string, comments: Uint8Array) => {
  self.sendSheetComments(sheetId, comments);
});

export const jsSheetCharts = guard('jsSheetCharts', (sheetId: string, charts: Uint8Array) => {
  self.sendSheetCharts(sheetId, charts);
});

export const jsValidationWarnings = guard('jsValidationWarnings', (warnings: Uint8Array) => {
  self.sendValidationWarnings(warnings);
});

export const jsRequestRowHeights = guard(
  'jsRequestRowHeights',
  (transactionId: string, sheetId: string, rows: string) => {
    self.sendRequestRowHeights(transactionId, sheetId, rows);
  }
);

export const jsMultiplayerSynced = guard('jsMultiplayerSynced', () => {
  self.sendMultiplayerSynced();
});

export const jsHashesRenderCells = guard('jsHashesRenderCells', (render_cells: Uint8Array) => {
  self.sendHashRenderCellsRender(render_cells);
});

export const jsHashesDirty = guard('jsHashesDirty', (dirtyHashes: Uint8Array) => {
  self.sendHashesDirtyRender(dirtyHashes);
});

export const jsClientMessage = guard('jsClientMessage', (message: string, severity: JsSnackbarSeverity) => {
  self.sendClientMessage(message, severity);
});

export const jsSendViewportBuffer = guard('jsSendViewportBuffer', (buffer: SharedArrayBuffer) => {
  self.sendViewportBuffer(buffer);
});

/**
 * Check if SharedArrayBuffer is available.
 * Called by Rust to determine whether to use SAB or fallback to async viewport.
 */
export const jsHasSharedArrayBuffer = guardReturn(
  'jsHasSharedArrayBuffer',
  (): boolean => {
    return typeof SharedArrayBuffer !== 'undefined';
  },
  false
);

/**
 * Get the cached viewport for non-SAB mode.
 * Called by Rust when SharedArrayBuffer is not available.
 * Returns a JSON string with viewport info, or null if not available.
 */
export const jsGetViewport = guardReturn(
  'jsGetViewport',
  (): string | null => {
    return self.getCachedViewportJson();
  },
  null
);

export const jsA1Context = guard('jsA1Context', (context: Uint8Array) => {
  self.sendA1Context(context);
});

export const jsSendDataTablesCache = guard('jsSendDataTablesCache', (sheetId: string, dataTablesCache: Uint8Array) => {
  self.sendDataTablesCache(sheetId, dataTablesCache);
});

export const jsSendContentCache = guard('jsSendContentCache', (sheetId: string, contentCache: Uint8Array) => {
  self.sendContentCache(sheetId, contentCache);
});

export const jsCodeRunningState = guard('jsCodeRunningState', (transactionId: string, codeOperations: string) => {
  self.sendCodeRunningState(transactionId, codeOperations);
});

export const jsPendingCellsState = guard('jsPendingCellsState', (pendingCells: string) => {
  self.sendPendingCellsState(pendingCells);
});

export const jsTimestamp = guardReturn(
  'jsTimestamp',
  (): bigint => {
    return BigInt(Date.now());
  },
  BigInt(0)
);

// High-resolution monotonic clock for the quadratic-core profiler. Workers
// expose performance.now() with sub-millisecond resolution.
export const jsPerformanceNow = guardReturn(
  'jsPerformanceNow',
  (): number => {
    return performance.now();
  },
  0
);

export const jsMergeCells = guard(
  'jsMergeCells',
  (sheetId: string, mergeCells: Uint8Array, dirtyHashes: Uint8Array) => {
    const clientCopy = new Uint8Array(mergeCells);
    self.sendMergeCells(sheetId, clientCopy);
    self.sendMergeCellsRender(sheetId, mergeCells, dirtyHashes);
  }
);
